
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type Category = {
  id: string;
  name: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
};

// Get all categories
export const useCategories = () => {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });
};

// Get main categories (those with null parent_id)
export const useMainCategories = () => {
  return useQuery({
    queryKey: ["categories", "main"],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .is("parent_id", null)
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
  });
};

// Get subcategories for a specific parent
export const useSubcategories = (parentId: string | null) => {
  return useQuery({
    queryKey: ["categories", "sub", parentId],
    queryFn: async (): Promise<Category[]> => {
      if (!parentId) return [];
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("parent_id", parentId)
        .order("name");
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!parentId,
  });
};

// Get category by ID
export const useCategoryById = (categoryId: string | null) => {
  return useQuery({
    queryKey: ["categories", categoryId],
    queryFn: async (): Promise<Category | null> => {
      if (!categoryId) return null;
      
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("id", categoryId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!categoryId,
  });
};

// Get full category path (breadcrumb)
export const useCategoryPath = (categoryId: string | null) => {
  return useQuery({
    queryKey: ["categories", "path", categoryId],
    queryFn: async (): Promise<Category[]> => {
      if (!categoryId) return [];
      
      const path: Category[] = [];
      let currentId = categoryId;
      
      // Fetch up to 2 levels (for our two-level hierarchy)
      for (let i = 0; i < 2; i++) {
        if (!currentId) break;
        
        const { data, error } = await supabase
          .from("categories")
          .select("*")
          .eq("id", currentId)
          .maybeSingle();
        
        if (error || !data) break;
        
        path.unshift(data); // Add to the beginning of the array
        currentId = data.parent_id;
      }
      
      return path;
    },
    enabled: !!categoryId,
  });
};

// Helper function to get category name by ID
export const getCategoryNameById = async (categoryId: string | null): Promise<string> => {
  if (!categoryId) return "Other";
  
  try {
    const { data } = await supabase
      .from("categories")
      .select("name")
      .eq("id", categoryId)
      .maybeSingle();
    
    return data?.name || "Other";
  } catch (error) {
    console.error("Error fetching category name:", error);
    return "Other";
  }
};

// Add mutation function to reset categories
export const useResetCategories = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (categories: {mains: {name: string, subcategories: string[]}[]}) => {
      try {
        // First fix the foreign key constraint issue
        const { data, error } = await supabase.functions.invoke("fix_category_constraint");
        
        if (error) {
          console.error("Error invoking fix_category_constraint edge function:", error);
          throw error;
        }
        
        // Check if "Other" category exists, if not, create it
        const { data: otherCategory, error: otherQueryError } = await supabase
          .from("categories")
          .select()
          .eq("name", "Other")
          .maybeSingle();
        
        if (otherQueryError) {
          console.error("Error checking for Other category:", otherQueryError);
          throw otherQueryError;
        }
        
        if (!otherCategory) {
          // Create "Other" category if it doesn't exist
          const { error: otherError } = await supabase
            .from("categories")
            .insert({ name: "Other" });
          
          if (otherError) {
            console.error("Error creating Other category:", otherError);
            throw otherError;
          }
        }
        
        // Then insert all main categories and their subcategories
        for (const mainCategory of categories.mains) {
          // Insert main category
          const { data: mainData, error: mainError } = await supabase
            .from("categories")
            .insert({ name: mainCategory.name })
            .select()
            .single();
          
          if (mainError) {
            console.error(`Error creating main category ${mainCategory.name}:`, mainError);
            throw mainError;
          }
          
          // Insert all subcategories for this main category
          if (mainData && mainCategory.subcategories.length > 0) {
            const subcategories = mainCategory.subcategories.map(subName => ({
              name: subName,
              parent_id: mainData.id
            }));
            
            const { error: subError } = await supabase
              .from("categories")
              .insert(subcategories);
            
            if (subError) {
              console.error(`Error creating subcategories for ${mainCategory.name}:`, subError);
              throw subError;
            }
          }
        }
        
        return { success: true };
      } catch (error) {
        console.error("Error in resetCategories:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    }
  });
};
